import { FC, useRef, useState } from 'react';
import { Button, ComboBox, InlineNotification, TextInput } from '@carbon/react';
import { useForm } from '@tanstack/react-form';
import { useAtom, useSetAtom } from 'jotai';
import type { ApiPayloads } from 'types/api-payloads';
import { ColorSwatch } from 'components/color-picker';
import {
  closeGeologicIntervalDrawerAtom,
  updateGeologicIntervalAtom,
  useLithologyItems,
} from './atoms';
import { OperatorNamingConventionsSection } from './operator-naming-conventions-section';
import { isValidHexColor } from './utils';
import type {
  EditGeologicIntervalFormValues,
  GeologicIntervalRow,
  PendingAddOnc,
} from './types';
import styles from './geologic-interval-drawer.module.scss';

type UpdateGeologicIntervalBody =
  ApiPayloads['updateGeologicInterval']['payload']['geologicInterval'];

export interface EditGeologicIntervalContentProps {
  target: GeologicIntervalRow;
  onUpdated: () => void;
}

// BE only accepts displayColor / age / lithology updates; basin and
// interval name are read-only. Staged ONC ops fold into the Save PATCH.
export const EditGeologicIntervalContent: FC<
  EditGeologicIntervalContentProps
> = ({ target, onUpdated }) => {
  const [updateState, updateInterval] = useAtom(updateGeologicIntervalAtom);
  const closeDrawer = useSetAtom(closeGeologicIntervalDrawerAtom);

  const isSubmitting = updateState.loading;
  // Local so reopening the drawer starts clean — the mutation atom keeps its last result.
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [pendingDeleteOncIds, setPendingDeleteOncIds] = useState<number[]>([]);
  const togglePendingDeleteOnc = (id: number) => {
    setPendingDeleteOncIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      return [...prev, id];
    });
  };
  const [pendingAddOncs, setPendingAddOncs] = useState<PendingAddOnc[]>([]);
  // Per-mount tempId counter — the drawer remounts on row change.
  const nextTempIdRef = useRef(0);
  const appendPendingAddOnc = (
    operator: { name: string | null; allOperators: boolean },
    targetZoneIdentifier: string
  ) => {
    const tempId = String(nextTempIdRef.current);
    nextTempIdRef.current += 1;
    setPendingAddOncs((prev) => [
      ...prev,
      { tempId, operator, targetZoneIdentifier },
    ]);
  };
  const removePendingAddOnc = (tempId: string) =>
    setPendingAddOncs((prev) => prev.filter((p) => p.tempId !== tempId));

  const lithologyItems = useLithologyItems();

  const defaultValues: EditGeologicIntervalFormValues = {
    age: String(target.age ?? ''),
    lithology: target.lithology ?? '',
    displayColor: target.displayColor ?? '#000000',
  };

  const form = useForm({
    defaultValues,
    onSubmit: ({ value }) => {
      setSubmitError(null);
      const oncAttributes = [
        ...pendingAddOncs.map((p) => ({
          operator: p.operator,
          targetZoneIdentifier: p.targetZoneIdentifier,
        })),
        ...pendingDeleteOncIds.map((id) => ({ id, _destroy: true })),
      ];
      const body: UpdateGeologicIntervalBody = {
        age: value.age.trim() ? Number(value.age) : null,
        lithology: value.lithology || null,
        ...(isValidHexColor(value.displayColor)
          ? { displayColor: value.displayColor }
          : {}),
        ...(oncAttributes.length > 0
          ? { operatorNamingConventionsAttributes: oncAttributes }
          : {}),
      };
      updateInterval({
        type: 'PATCH',
        url: `/geologic_intervals/${target.id}`,
        data: { geologicInterval: body },
        onSuccess: () => {
          onUpdated();
          closeDrawer();
        },
        onError: (errorData) => {
          const firstError = errorData?.errors?.[0];
          setSubmitError(
            firstError?.message ?? 'Failed to update geologic interval.'
          );
        },
      });
    },
  });

  return (
    <form
      className={styles.formContainer}
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
    >
      {submitError && (
        <div className={styles.errorBanner}>
          <InlineNotification
            kind="error"
            title="Save failed"
            subtitle={submitError}
            onCloseButtonClick={() => setSubmitError(null)}
            lowContrast
          />
        </div>
      )}
      <div className={styles.scrollableBody}>
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Interval Details</h3>
          <div className={styles.fieldRow}>
            <form.Field name="age">
              {(field) => (
                <TextInput
                  id="edit-age"
                  labelText="Age (MYA, optional)"
                  placeholder="e.g. 280"
                  value={field.state.value}
                  onChange={(e) =>
                    field.handleChange(e.target.value.replace(/[^\d.]/g, ''))
                  }
                  size="md"
                />
              )}
            </form.Field>
            <form.Field name="lithology">
              {(field) => (
                <ComboBox
                  id="edit-lithology"
                  titleText="Lithology (optional)"
                  placeholder="Search lithologies…"
                  items={lithologyItems}
                  itemToString={(item) => item ?? ''}
                  selectedItem={field.state.value || null}
                  onChange={({ selectedItem }) =>
                    field.handleChange(selectedItem ?? '')
                  }
                  size="md"
                />
              )}
            </form.Field>
          </div>
        </section>

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Display Color</h3>
          <form.Field name="displayColor">
            {(field) => {
              const valid = isValidHexColor(field.state.value);
              return (
                <div className={styles.colorRow}>
                  <ColorSwatch
                    color={valid ? field.state.value : '#000000'}
                    onChange={field.handleChange}
                  />
                  <span className={styles.hexLabel}>
                    {valid ? field.state.value : '—'}
                  </span>
                </div>
              );
            }}
          </form.Field>
        </section>

        <OperatorNamingConventionsSection
          target={target}
          pendingDeleteIds={pendingDeleteOncIds}
          onTogglePendingDelete={togglePendingDeleteOnc}
          pendingAddOncs={pendingAddOncs}
          onAddPending={appendPendingAddOnc}
          onRemovePending={removePendingAddOnc}
        />
      </div>

      <div className={styles.footer}>
        <Button kind="secondary" type="button" onClick={closeDrawer}>
          Cancel
        </Button>
        <form.Subscribe selector={(s) => s.isDirty}>
          {(fieldsDirty) => {
            const oncDirty =
              pendingAddOncs.length > 0 || pendingDeleteOncIds.length > 0;
            const isDirty = fieldsDirty || oncDirty;
            return (
              <Button
                kind="primary"
                type="submit"
                disabled={isSubmitting || !isDirty}
              >
                {isSubmitting ? 'Saving…' : 'Save'}
              </Button>
            );
          }}
        </form.Subscribe>
      </div>
    </form>
  );
};
